import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, ArrowRight, CalendarCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { SCHEDULER_URL } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WELCOME = "Hi, I'm Casey's Rhythm Guide. Whether you're planning an event, a classroom, or care for a loved one, I can point you to the right program. What brings you here today?";

const QUICK_CHIPS = [
  { id: "keynote", label: "Plan a keynote", send: "I'm planning a keynote or event for a large group." },
  { id: "classroom", label: "For my classroom", send: "I'm interested in rhythm sessions for my early-childhood classroom." },
  { id: "memory-care", label: "Memory care", send: "I'm looking for a program for a senior living or memory care community." },
];

// In-chat booking deep-links, surfaced when the guide names a program.
const PROGRAM_CTAS = [
  { key: "drumtalk", match: /drumtalk/i, to: "/drumtalk", label: "Reserve your date" },
  { key: "pals", match: /percussion pals/i, to: "/percussion-pals", label: "Get the activity guide" },
  { key: "pathways", match: /percussion pathways/i, to: "/percussion-pathways", label: "Request the info kit" },
  { key: "trap", match: /trap learning/i, to: "/program/trap-learning", label: "Explore TRAP Learning" },
];

function getAnonId() {
  let id = localStorage.getItem("cm_chat_sid");
  if (!id) {
    id = "anon:" + ((crypto.randomUUID && crypto.randomUUID()) || String(Date.now()));
    localStorage.setItem("cm_chat_sid", id);
  }
  return id;
}

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadedFor, setLoadedFor] = useState(null);
  const scrollRef = useRef(null);
  const endRef = useRef(null);

  const sessionId = user ? `user:${user.user_id}` : getAnonId();

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: "smooth" }));
  }, []);

  // Load history when the panel first opens (or when the session identity changes)
  useEffect(() => {
    if (!open || loadedFor === sessionId) return;
    (async () => {
      try {
        const res = await fetch(`${API}/chat/${encodeURIComponent(sessionId)}/history`, { credentials: "include" });
        const data = await res.json();
        setMessages(data.messages && data.messages.length ? data.messages : [{ role: "assistant", content: WELCOME }]);
      } catch {
        setMessages([{ role: "assistant", content: WELCOME }]);
      } finally {
        setLoadedFor(sessionId);
        scrollToBottom();
      }
    })();
  }, [open, sessionId, loadedFor, scrollToBottom]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const send = async (preset) => {
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || busy) return;
    if (typeof preset !== "string") setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/${encodeURIComponent(sessionId)}/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + evt.delta };
                return copy;
              });
            }
          } catch { /* ignore keep-alive / partial */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I couldn't connect just now. Please try again in a moment." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <motion.button
        data-testid="chat-launcher"
        onClick={() => setOpen((o) => !o)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.6, type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        aria-label="Chat with Casey's Rhythm Guide"
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full grid place-items-center shadow-[0_10px_40px_rgba(139,92,246,0.5)]"
        style={{ background: "linear-gradient(135deg, var(--primary), #6d28d9)" }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={22} className="text-white" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle size={24} className="text-white" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-5 z-50 w-[min(92vw,380px)] h-[min(70vh,560px)] flex flex-col glass shape-r overflow-hidden border border-white/10 shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/10" style={{ background: "linear-gradient(120deg, rgba(139,92,246,0.22), transparent)" }}>
              <span className="grid place-items-center h-9 w-9 rounded-full" style={{ background: "linear-gradient(135deg, var(--primary), #6d28d9)" }}>
                <Sparkles size={16} className="text-white" />
              </span>
              <div className="leading-tight">
                <p className="font-serif text-[15px]">Casey's Rhythm Guide</p>
                <p className="font-mono text-[9px] tracking-[0.15em] uppercase text-fg/45">Ask about programs &amp; booking</p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => {
                const streaming = busy && i === messages.length - 1;
                return (
                  <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`} data-testid={`chat-msg-${m.role}`}>
                    <div
                      className={`max-w-[82%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${m.role === "user" ? "text-white rounded-br-sm" : "bg-white/8 text-fg/90 rounded-bl-sm"}`}
                      style={m.role === "user" ? { background: "linear-gradient(135deg, var(--primary), #6d28d9)" } : undefined}
                    >
                      {m.content || (streaming ? <TypingDots /> : "")}
                    </div>
                    {m.role === "assistant" && m.content && !streaming && (
                      <ChatCtas content={m.content} />
                    )}
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>

            {/* Quick-reply starter chips */}
            {messages.length <= 1 && !busy && (
              <div className="px-3 pb-1 flex flex-wrap gap-2" data-testid="chat-quick-replies">
                {QUICK_CHIPS.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`chat-chip-${c.id}`}
                    onClick={() => send(c.send)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border border-primary-c/50 text-primary-c hover:bg-primary-c/10 transition-colors"
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            )}

            {/* Composer */}
            <div className="p-3 border-t border-white/10">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  placeholder="Type your message…"
                  className="flex-1 resize-none max-h-28 shape-r bg-black/25 border border-white/15 focus:outline-none focus:border-primary-c p-2.5 text-sm text-fg placeholder:text-fg/30 transition-colors"
                />
                <button
                  data-testid="chat-send"
                  onClick={send}
                  disabled={busy || !input.trim()}
                  className="shrink-0 h-10 w-10 grid place-items-center rounded-xl transition-opacity disabled:opacity-40 hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, var(--primary), #6d28d9)" }}
                  aria-label="Send message"
                >
                  <Send size={17} className="text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex gap-1 py-1" data-testid="chat-typing">
      {[0, 1, 2].map((i) => (
        <span key={i} className="h-1.5 w-1.5 rounded-full bg-fg/50 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
      ))}
    </span>
  );
}

function ChatCtas({ content }) {
  const programs = PROGRAM_CTAS.filter((c) => c.match.test(content));
  const showCall = /cadence call|book|booking/i.test(content);
  if (!programs.length && !showCall) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2 max-w-[90%]" data-testid="chat-ctas">
      {programs.map((c) => (
        <Link
          key={c.key}
          to={c.to}
          data-testid={`chat-cta-${c.key}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white transition-opacity hover:opacity-90"
          style={{ background: "linear-gradient(135deg, var(--primary), #6d28d9)" }}
        >
          {c.label} <ArrowRight size={13} />
        </Link>
      ))}
      {showCall && (
        <a
          href={SCHEDULER_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="chat-cta-cadence"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-primary-c/50 text-primary-c hover:bg-primary-c/10 transition-colors"
        >
          <CalendarCheck size={13} /> Book a Cadence Call
        </a>
      )}
    </div>
  );
}
